import { useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { getEnv } from "../../../AppConfigs";
import { CreateNotification } from "../../../components/notifier";
import { RegExp } from "../../../constants";
import { addFolderValidationSchema } from "../../../constants/validationSchema";
import { useSearchParams } from "../../../hook/use-search-params";
import { FileService } from "../../../services/file/file.service";
import { useDocDirectory, useUpdateDocDirectory } from "../hooks";
import useCreateFolder from "../hooks/documents/use-create-folder";

interface AddFolderModalProps {
  onClose: () => void;
  onSuccess?: (type: "create" | "update") => void | Promise<void>;
  isOpen: boolean;
}

const AddFolderModal = (props: AddFolderModalProps) => {
  const isForbidden = useSelector((state: any) => state.main.isForbidden);
  const { onClose, onSuccess, isOpen } = props;
  const { searchParams } = useSearchParams();
  const folderId = searchParams.get("folderId");
  const isAddRoot = !!searchParams.get("addRoot");

  const { mutateAsync: updateDocDirectoryAsync } = useUpdateDocDirectory();
  const { mutateAsync: createFolderAsync } = useCreateFolder();
  const { hashedData, treeData, data = [] } = useDocDirectory();

  const queryClient = useQueryClient();

  const handleOnSubmit = async (values: any) => {
    let docPosition: number;
    const driveItemTreeData = treeData.map((item: any) => hashedData.get(item.key));
    // logic calculate doc postion
    if (isAddRoot) {
      docPosition = Math.max(...driveItemTreeData.map((item: any) => item?.docPosition ?? 0)) + 1;
    } else {
      const parentNode = hashedData.get(folderId);
      if (parentNode?.folder?.childCount === 0) {
        docPosition = 0;
      } else {
        const parentNodeChildren = data.filter((item) => item.parentReference.id === folderId);
        docPosition = Math.max(...parentNodeChildren.map((item: any) => item?.docPosition ?? 0)) + 1;
      }
    }

    const rootFolderId = getEnv("REACT_APP_DOCUMENT_FOLDER_ROOT_ID");
    const { foldername } = values;
    try {
      if (!RegExp.folderNameRegex.test(foldername.trim())) {
        CreateNotification({
          title: "Thông báo",
          body: `Thư mục chứa ký tự không hợp lệ vui lòng thử lại.`,
          icon: "/icons/warning.png",
        });
        return;
      }

      const response = await FileService.addFolder(
        {
          name: foldername.trim(),
          folder: {},
        },
        !isAddRoot ? folderId! : rootFolderId,
      );
      const driveItemResponse = await FileService.getDriveItem({ itemId: response.id });
      await updateDocDirectoryAsync({ itemId: driveItemResponse.data.listItem.id, docPosition });

      await createFolderAsync({
        isPublicFolder: false,
        key: response.id,
        name: foldername.trim(),
        parentKey: !isAddRoot ? folderId! : undefined,
        position: docPosition ?? undefined,
      });

      CreateNotification({ title: "Thành công", body: `Tạo folder thành công.`, icon: "/icons/success.png" });
      await queryClient.invalidateQueries({ queryKey: ["folders"], type: "all" });
      await queryClient.invalidateQueries({ queryKey: ["publish-folders-new"], type: "all" });
      await onSuccess?.("create");
      onClose();
    } catch (error) {
      if (isForbidden) {
        CreateNotification({
          title: "Thông báo",
          body: `Bạn không có quyền để tạo thư mục. Vui lòng liên hệ Admin để được hỗ trợ.`,
          icon: "/icons/error.png",
        });
      } else {
        CreateNotification({
          title: "Thông báo",
          body: `Không thể tạo thư mục, vui lòng thử lại.`,
          icon: "/icons/error.png",
        });
        console.log(error);
      }
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered size={"lg"} className="add-folder-modal">
      <Formik
        validationSchema={addFolderValidationSchema}
        onSubmit={handleOnSubmit}
        initialValues={{
          foldername: "",
        }}
      >
        {({ handleSubmit, handleChange, values, errors }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>{"Tạo thư mục mới"}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-4 px-4">
              <Form.Group as={Row} controlId="validationCustom01">
                <Form.Label column sm={2} className="card-label">
                  {"Tên thư mục"}
                </Form.Label>
                <Col sm={10}>
                  <Form.Control
                    required
                    type="text"
                    placeholder="Ex: Quản lý tài liệu"
                    name="foldername"
                    value={values.foldername}
                    onChange={handleChange}
                    isInvalid={!!errors.foldername}
                  />
                  <Form.Control.Feedback type="invalid">{errors.foldername}</Form.Control.Feedback>
                </Col>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={onClose} variant="secondary">
                Hủy
              </Button>
              <Button type="submit" variant="info">
                {"Tạo thư mục"}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddFolderModal;
